import * as React from 'react'
import type { Hit } from 'instantsearch.js/es/types'
import { tw } from 'utils/classnames'
import { useOnClickOutside } from 'hooks/use-on-click-outside'

import {
  MIN_REFINEMENT_LENGTH,
  DEFAULT_ALGOLIA_LANGUAGE,
  mapLocaleToContentLanguage,
} from './algolia.configs'
import { SearchInput } from './search-input'
import type { AlgoliaContentHit, SearchProduct } from './search.types'
import { useRouterUtil, useLocale } from '../../contexts/header-config-context'
import { RecentSearches } from './recent-searches'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { AppRoute } from '../../utils/route'

interface AutocompleteProps {
  autofocus?: boolean
  renderResult?: (hit: Hit<SearchProduct | AlgoliaContentHit>) => React.ReactNode
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  hits: Hit<SearchProduct | AlgoliaContentHit>[]
  currentRefinement: string
  refine: (value: string) => void
  className?: string
  onChange: (newValue: string) => void
  aiSearchSlot?: React.ReactNode | ((query: string) => React.ReactNode)
}

// Ideally this would be a fully accessible autocomplete component, but currently Radix UI doesn't have a good fit.
// However a new primitive for this use case is being developed: https://github.com/radix-ui/primitives/issues/1342
export function Autocomplete({
  autofocus,
  className,
  currentRefinement,
  hits,
  renderResult,
  onChange,
  onSubmit,
  aiSearchSlot,
}: AutocompleteProps) {
  const formRef = React.useRef<HTMLFormElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [focus, setFocus] = React.useState(false)
  const [showResults, setShowResults] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)
  const pathname = useRouterUtil().pathname
  const locale = useLocale()
  const isResultsVisible =
    currentRefinement.length >= MIN_REFINEMENT_LENGTH &&
    renderResult &&
    hits.length > 0 &&
    showResults

  useOnClickOutside(formRef, () => {
    setShowResults(false)
    setFocus(false)
  })

  React.useEffect(() => {
    if (!isResultsVisible) {
      return
    }

    sendAmplitudeEvent(AmplitudeEvents.PRODUCTS_SEARCHED, {
      query: currentRefinement,
      source: 'header',
    })
  }, [isResultsVisible, currentRefinement])

  // force close autocomplete when route changes
  React.useEffect(() => {
    setShowResults(false)
  }, [pathname])

  // reset selection when hits change or query changes
  React.useEffect(() => {
    setSelectedIndex(-1)
  }, [hits, currentRefinement])

  React.useEffect(() => {
    // close autocomplete when focus is lost, but with a delay to allow for clicks on the autocomplete results
    const timeout =
      !focus &&
      setTimeout(() => {
        setShowResults(false)
      }, 150)
    return () => {
      timeout && clearTimeout(timeout)
    }
  }, [focus])

  const onKeyPress = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        setShowResults(false)
        setSelectedIndex(-1)
        const activeElement = document.activeElement as HTMLElement
        if (activeElement) {
          activeElement.blur()
        }
        return
      }

      if (!isResultsVisible || hits.length === 0) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setSelectedIndex(prev => (prev < hits.length - 1 ? prev + 1 : prev))
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < hits.length) {
          const selectedHit = hits[selectedIndex]
          // Navigate to the selected hit
          if ('slug' in selectedHit) {
            const algoliaLang = mapLocaleToContentLanguage.get(locale) || DEFAULT_ALGOLIA_LANGUAGE
            const prodUrl = `${AppRoute.PROD}${selectedHit.slug ? selectedHit.slug[algoliaLang] : ''}-${selectedHit.objectID}/`
            window.location.href = prodUrl
          } else if (selectedHit.url) {
            const url =
              // oxlint-disable-next-line no-negated-condition, unicorn/prefer-includes
              selectedHit.url.indexOf('creativefabrica.com') !== -1
                ? selectedHit.url.split('creativefabrica.com')[1]
                : selectedHit.url || ''
            window.location.href = url
          }
        } else {
          // Submit form if no item is selected
          // oxlint-disable-next-line no-lonely-if
          if (formRef.current) {
            formRef.current.requestSubmit()
          }
        }
        return
      }
    },
    [isResultsVisible, hits, selectedIndex, locale],
  )

  return (
    <div className={tw('relative flex grow z-[1]', className)} data-testid="header-search">
      <form
        ref={formRef}
        onSubmit={e => {
          onSubmit(e)
          setFocus(false)
          inputRef.current?.blur()
        }}
        className="w-full"
      >
        <SearchInput
          ref={inputRef}
          autofocus={autofocus}
          value={currentRefinement}
          onChange={onChange}
          onKeyPress={onKeyPress}
          onFocus={() => {
            setFocus(true)
            setShowResults(true)
          }}
          aiSearchSlot={
            typeof aiSearchSlot === 'function' ? aiSearchSlot(currentRefinement) : aiSearchSlot
          }
        />
        {isResultsVisible ? (
          <div
            key="results"
            id="search-autocomplete-results"
            data-testid="header-search-autocomplete"
            className={tw(
              'z-cf-menu absolute top-[50px]',
              'w-full px-2 py-3',
              'bg-cf-white shadow-cf-elevation-light-5 rounded-2',
            )}
          >
            {hits.map((hit, index) => (
              <div
                key={hit.objectID}
                className={tw(
                  'rounded-2',
                  index === selectedIndex ? 'bg-cf-lavender' : 'bg-transparent',
                )}
              >
                {renderResult ? renderResult(hit) : null}
              </div>
            ))}
          </div>
        ) : null}
        {!isResultsVisible && focus ? (
          <RecentSearches
            onFillQueryClick={query => {
              onChange(query)
              setTimeout(() => {
                inputRef.current?.focus()
              })
            }}
            onItemClick={query => {
              onChange(query)
              setTimeout(() => {
                formRef.current?.requestSubmit()
              })
            }}
          />
        ) : null}
      </form>
    </div>
  )
}
